import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

/**
 * SmartMenu — 3-dot dropdown that auto-flips above/below based on viewport space.
 * Renders the open menu via React Portal into document.body so it escapes any
 * `transform`/`overflow` containing block on ancestor elements.
 *
 * Props:
 *  - items: [{ label, icon, onClick, danger?, hidden?, testId? }]
 *  - testId: data-testid prefix
 *  - align: "right" (default) | "left"
 *  - className: optional class on the trigger button
 *  - itemHeight: estimate per item (default 48)
 *  - menuWidth: default 240
 *  - triggerLabel: aria-label
 *  - stopPropagation: bubble blocker for parent clickables (default true)
 */
export default function SmartMenu({
  items = [],
  testId = "smart-menu",
  align = "right",
  className = "",
  itemHeight = 48,
  menuWidth = 240,
  triggerLabel = "More actions",
  stopPropagation = true,
  renderTrigger,
}) {
  const visible = items.filter((i) => !i.hidden);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);

  const GAP = 6;
  const estHeight = visible.length * itemHeight + 8;

  const compute = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const flipUp = spaceBelow < estHeight + GAP;
    const horiz = align === "left"
      ? { left: Math.max(8, r.left) }
      : { right: Math.max(8, window.innerWidth - r.right) };
    if (flipUp) {
      setPos({ ...horiz, bottom: window.innerHeight - r.top + GAP });
    } else {
      setPos({ ...horiz, top: r.bottom + GAP });
    }
  };

  const toggle = (e) => {
    if (stopPropagation) { e.preventDefault(); e.stopPropagation(); }
    if (open) { setOpen(false); return; }
    compute();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const h = () => compute();
    window.addEventListener("resize", h);
    window.addEventListener("scroll", h, true);
    return () => {
      window.removeEventListener("resize", h);
      window.removeEventListener("scroll", h, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="relative inline-block" onClick={(e) => stopPropagation && e.stopPropagation()}>
      {renderTrigger ? (
        renderTrigger({ ref: triggerRef, onClick: toggle, open, testId: `${testId}-trigger` })
      ) : (
        <button ref={triggerRef} type="button" data-testid={`${testId}-trigger`}
          onClick={toggle}
          aria-label={triggerLabel} aria-expanded={open} aria-haspopup="menu"
          className={`min-h-[48px] w-12 border-2 hover:bg-safety-yellow hover:text-black inline-flex items-center justify-center transition-colors ${
            open ? "bg-safety-yellow text-black border-black" : "border-foreground dark:border-safety-yellow"
          } ${className}`}>
          <MoreVertical className="w-5 h-5" strokeWidth={2.5} />
        </button>
      )}
      {open && pos && typeof document !== "undefined" && createPortal(
        <>
          <div className="fixed inset-0 z-[70]"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            onMouseDown={(e) => e.stopPropagation()}
            aria-hidden="true" />
          <div role="menu" data-testid={`${testId}-content`}
            style={{
              position: "fixed",
              top: pos.top, bottom: pos.bottom, left: pos.left, right: pos.right,
              width: menuWidth, zIndex: 80,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="bg-background border-2 border-foreground dark:border-safety-yellow">
            {visible.map((item, idx) => {
              const Icon = item.icon;
              return (
                <button key={item.label} type="button" role="menuitem"
                  data-testid={item.testId || `${testId}-${idx}`}
                  onClick={(e) => { e.stopPropagation(); setOpen(false); item.onClick(e); }}
                  className={`w-full text-left min-h-[48px] px-4 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 ${
                    idx < visible.length - 1 ? "border-b-2 border-foreground/20 dark:border-safety-yellow/40" : ""
                  } ${item.danger
                    ? "text-status-expired hover:bg-status-expired hover:text-white"
                    : "hover:bg-safety-yellow hover:text-black"}`}>
                  {Icon && <Icon className="w-4 h-4" strokeWidth={2.5} />} {item.label}
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
